import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
} from '@angular/core';
import { Subscription, forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ProjectService } from '../../Services/project.service';
import {
  WORK_INSIGHTS_ENABLED,
  INSIGHTS_WINDOW_DAYS,
  MEANINGFUL_ACTIVE_DAY_SEC,
  SHORT_DAY_MEDIAN_RATIO,
  LONG_DAY_MEDIAN_RATIO,
} from './work-insights.constants';
import {
  ActivitySourceRow,
  CommunicationTile,
  CompositionSlice,
  DeepWorkTile,
  InsightsDailyPoint,
  PeriodDelta,
  TrendBar,
  WeekdayBar,
  WorkloadTile,
} from './work-insights-section.types';

const WEEKDAY_ORDER = [2, 3, 4, 5, 6, 7, 1];
const WEEKDAY_LABELS: Record<number, string> = {
  1: 'Sun',
  2: 'Mon',
  3: 'Tue',
  4: 'Wed',
  5: 'Thu',
  6: 'Fri',
  7: 'Sat',
};

@Component({
  selector: 'app-work-insights-section',
  templateUrl: './work-insights-section.component.html',
  styleUrls: ['./work-insights-section.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class WorkInsightsSectionComponent implements OnChanges, OnDestroy {
  @Input() userId: number | string | null = null;

  readonly enabled = WORK_INSIGHTS_ENABLED;
  readonly windowDays = INSIGHTS_WINDOW_DAYS;

  expanded = false;
  loading = false;
  loaded = false;

  trendBars: TrendBar[] = [];
  trendMeaningfulDays = 0;
  trendAverageHours = 0;
  trendTotalHours = 0;
  trendStartLabel = '';
  trendEndLabel = '';
  trendVerdict = '';
  weekdayBars: WeekdayBar[] = [];
  composition: CompositionSlice | null = null;
  priorComposition: CompositionSlice | null = null;
  periodDelta: PeriodDelta | null = null;
  sources: ActivitySourceRow[] = [];
  appSources: ActivitySourceRow[] = [];
  webSources: ActivitySourceRow[] = [];
  deepWork: DeepWorkTile | null = null;
  communication: CommunicationTile | null = null;
  workload: WorkloadTile | null = null;
  hasReviewSignal = false;

  private requestSubscription?: Subscription;

  constructor(
    private readonly projectService: ProjectService,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['userId'] && !changes['userId'].firstChange) {
      this.resetData();
      if (this.expanded) this.fetchAll();
    }
  }

  ngOnDestroy(): void {
    this.requestSubscription?.unsubscribe();
  }

  toggle(): void {
    this.expanded = !this.expanded;
    if (this.expanded && !this.loaded && !this.loading) {
      this.fetchAll();
    }
  }

  retry(): void {
    this.resetData();
    this.fetchAll();
  }

  formatHours(hours: number | null | undefined, digits = 1): string {
    if (hours == null || !Number.isFinite(hours)) return '—';
    const abs = Math.abs(hours);
    if (abs < 0.05) return '0h';
    if (abs < 1) return `${Math.round(hours * 60)}m`;
    return `${hours.toFixed(digits)}h`;
  }

  formatDeltaHours(hours: number | null): string {
    if (hours == null || !Number.isFinite(hours) || Math.abs(hours) < 0.05) return '—';
    const sign = hours > 0 ? '+' : '−';
    return `${sign}${this.formatHours(Math.abs(hours))}`;
  }

  formatDeltaPct(pct: number | null): string {
    if (pct == null || !Number.isFinite(pct)) return '—';
    const sign = pct > 0 ? '+' : pct < 0 ? '−' : '';
    return `${sign}${Math.abs(pct).toFixed(0)}%`;
  }

  deltaTone(value: number | null): string {
    if (value == null || Math.abs(value) < 0.05) return 'flat';
    return value > 0 ? 'up' : 'down';
  }

  private resetData(): void {
    this.requestSubscription?.unsubscribe();
    this.loaded = false;
    this.loading = false;
    this.trendBars = [];
    this.trendMeaningfulDays = 0;
    this.trendAverageHours = 0;
    this.trendTotalHours = 0;
    this.trendStartLabel = '';
    this.trendEndLabel = '';
    this.trendVerdict = '';
    this.weekdayBars = [];
    this.composition = null;
    this.priorComposition = null;
    this.periodDelta = null;
    this.sources = [];
    this.appSources = [];
    this.webSources = [];
    this.deepWork = null;
    this.communication = null;
    this.workload = null;
    this.hasReviewSignal = false;
    this.cdr.markForCheck();
  }

  private fetchAll(): void {
    const employeeId = Number(this.userId);
    if (!Number.isFinite(employeeId) || employeeId <= 0) return;

    const endDate = this.formatDate(new Date());
    const startDate = this.daysAgo(INSIGHTS_WINDOW_DAYS - 1);
    const priorEnd = this.daysAgo(INSIGHTS_WINDOW_DAYS);
    const priorStart = this.daysAgo(INSIGHTS_WINDOW_DAYS * 2 - 1);
    const dates = { start_date: startDate, end_date: endDate };
    const priorDates = { start_date: priorStart, end_date: priorEnd };
    const nullOnError = catchError(() => of(null));

    this.loading = true;
    this.cdr.markForCheck();

    this.requestSubscription = forkJoin({
      drilldown: this.projectService.getEmployeeProfileDrilldown(employeeId, {
        ...dates,
        compare_with: 'previous_period',
      }).pipe(nullOnError),
      prior: this.projectService.getEmployeeProfileDrilldown(employeeId, priorDates).pipe(nullOnError),
      deepWork: this.projectService.getEmployeeProfileAdvancedSection(employeeId, 'deep_work', dates).pipe(nullOnError),
      communication: this.projectService.getEmployeeProfileAdvancedSection(employeeId, 'communication', dates).pipe(nullOnError),
      workload: this.projectService.getEmployeeProfileAdvancedSection(employeeId, 'workload_burnout', dates).pipe(nullOnError),
      attention: this.projectService.getEmployeeProfileAdvancedSection(employeeId, 'attention_signals', dates).pipe(nullOnError),
    }).subscribe((res: any) => {
      const pattern: InsightsDailyPoint[] = res.drilldown?.data?.activity_pattern ?? [];
      this.trendBars = this.buildFilledWindow(pattern);
      const activeBars = this.trendBars.filter((b) => b.hasActivity);
      this.trendMeaningfulDays = this.trendBars.filter((b) => b.shape !== 'off' && b.shape !== 'light').length;
      this.trendTotalHours = activeBars.reduce((total, bar) => total + bar.hours, 0);
      this.trendAverageHours = this.trendMeaningfulDays
        ? this.trendBars.filter((b) => b.shape !== 'off' && b.shape !== 'light').reduce((t, b) => t + b.hours, 0)
          / this.trendMeaningfulDays
        : 0;
      this.trendStartLabel = this.trendBars[0]?.label ?? '';
      this.trendEndLabel = this.trendBars[this.trendBars.length - 1]?.label ?? '';
      this.trendVerdict = this.buildVerdict(this.trendBars);

      this.weekdayBars = this.buildWeekdayBars(res.drilldown?.data?.weekday_pattern ?? []);
      this.composition = this.buildComposition(res.drilldown?.data?.summary);
      this.priorComposition = this.buildComposition(res.prior?.data?.summary);
      this.periodDelta = this.buildPeriodDelta(this.composition, this.priorComposition);
      const splitSources = this.buildSplitSources(res.drilldown?.data?.activity_sources ?? []);
      this.appSources = splitSources.apps;
      this.webSources = splitSources.websites;
      this.sources = [...splitSources.apps, ...splitSources.websites];

      this.deepWork = this.rowForEmployee(res.deepWork, employeeId);
      this.communication = this.rowForEmployee(res.communication, employeeId);
      this.workload = this.rowForEmployee(res.workload, employeeId);

      const attention = this.rowForEmployee(res.attention, employeeId);
      this.hasReviewSignal = attention?.status === 'needs_review';

      this.loading = false;
      this.loaded = true;
      this.cdr.markForCheck();
    });
  }

  private rowForEmployee(response: any, employeeId: number): any | null {
    const rows = response?.data?.rows;
    if (!Array.isArray(rows) || !rows.length) return null;
    const match = rows.find((row: any) => Number(row?.employee_id) === employeeId);
    return match ?? (rows.length === 1 ? rows[0] : null);
  }

  private buildFilledWindow(pattern: InsightsDailyPoint[]): TrendBar[] {
    const byDate = new Map<string, InsightsDailyPoint>();
    for (const point of pattern ?? []) {
      const iso = String(point?.bucket ?? '').slice(0, 10);
      if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) byDate.set(iso, point);
    }

    const days: Array<{ iso: string; hours: number; isWeekend: boolean }> = [];
    for (let offset = INSIGHTS_WINDOW_DAYS - 1; offset >= 0; offset -= 1) {
      const iso = this.daysAgo(offset);
      const hours = Math.max(0, Number(byDate.get(iso)?.active_seconds ?? 0) / 3600);
      const weekday = new Date(`${iso}T12:00:00`).getDay();
      days.push({ iso, hours, isWeekend: weekday === 0 || weekday === 6 });
    }

    const meaningful = days.filter((d) => d.hours * 3600 >= MEANINGFUL_ACTIVE_DAY_SEC).map((d) => d.hours);
    const median = this.median(meaningful);
    const peak = Math.max(...days.map((d) => d.hours), 1);

    return days.map((day) => {
      const seconds = day.hours * 3600;
      let shape: TrendBar['shape'] = 'off';
      if (seconds <= 0) shape = 'off';
      else if (seconds < MEANINGFUL_ACTIVE_DAY_SEC) shape = 'light';
      else if (median > 0 && day.hours <= median * SHORT_DAY_MEDIAN_RATIO) shape = 'short';
      else if (median > 0 && day.hours >= median * LONG_DAY_MEDIAN_RATIO) shape = 'long';
      else shape = 'typical';

      const shapeLabel = {
        off: 'no tracked activity',
        light: 'light activity (under 15 min)',
        short: 'shorter than this person\'s typical day',
        typical: 'around this person\'s typical day',
        long: 'longer than this person\'s typical day',
      }[shape];

      return {
        iso: day.iso,
        label: this.shortDate(day.iso),
        hours: day.hours,
        heightPct: shape === 'off' ? 4 : Math.max(8, Math.round((day.hours / peak) * 100)),
        hasActivity: day.hours > 0,
        isWeekend: day.isWeekend,
        shape,
        tooltip: `${day.iso}: ${this.formatHours(day.hours)} · ${shapeLabel}`,
      };
    });
  }

  private buildWeekdayBars(rows: Array<{ weekday: number; active_seconds: number }>): WeekdayBar[] {
    const byDay = new Map<number, number>();
    for (const row of rows ?? []) {
      byDay.set(Number(row.weekday), Math.max(0, Number(row.active_seconds ?? 0) / 3600));
    }
    const peak = Math.max(...WEEKDAY_ORDER.map((key) => byDay.get(key) ?? 0), 1);
    return WEEKDAY_ORDER.map((key) => {
      const hours = byDay.get(key) ?? 0;
      return {
        key,
        label: WEEKDAY_LABELS[key],
        hours,
        heightPct: hours > 0 ? Math.max(8, Math.round((hours / peak) * 100)) : 4,
        tooltip: `${WEEKDAY_LABELS[key]}: ${this.formatHours(hours)} active across the window`,
      };
    });
  }

  private buildComposition(summary: { active_seconds?: number; idle_seconds?: number } | null | undefined): CompositionSlice | null {
    const active = Math.max(0, Number(summary?.active_seconds ?? 0));
    const idle = Math.max(0, Number(summary?.idle_seconds ?? 0));
    const logged = active + idle;
    if (logged <= 0) return null;
    return {
      activePct: (active / logged) * 100,
      idlePct: (idle / logged) * 100,
      activeHours: active / 3600,
      idleHours: idle / 3600,
      loggedHours: logged / 3600,
    };
  }

  private buildPeriodDelta(current: CompositionSlice | null, prior: CompositionSlice | null): PeriodDelta | null {
    if (!current || !prior || prior.activeHours < 0.05) return null;
    const activePct = ((current.activeHours - prior.activeHours) / prior.activeHours) * 100;
    return {
      activePct,
      loggedHoursDelta: current.loggedHours - prior.loggedHours,
      label: activePct <= -20
        ? 'Active time is well below the previous 30 days.'
        : activePct >= 20
          ? 'Active time is well above the previous 30 days.'
          : 'Active time is close to the previous 30 days.',
    };
  }

  private buildSplitSources(rows: any[]): { apps: ActivitySourceRow[]; websites: ActivitySourceRow[] } {
    const mapped = (rows ?? [])
      .map((row) => ({
        name: this.displaySourceName(row?.source_name ?? row?.name),
        type: String(row?.source_type ?? row?.type ?? ''),
        hours: Math.max(0, Number(row?.active_seconds ?? row?.seconds ?? 0) / 3600),
        previousHours: row?.previous_seconds == null ? null : Number(row.previous_seconds) / 3600,
      }))
      .filter((row) => row.hours > 0)
      .sort((a, b) => b.hours - a.hours);

    return {
      apps: this.decorateSources(mapped.filter((row) => !this.isWebSource(row.type)).slice(0, 5)),
      websites: this.decorateSources(mapped.filter((row) => this.isWebSource(row.type)).slice(0, 5)),
    };
  }

  private decorateSources(
    mapped: Array<{ name: string; type: string; hours: number; previousHours: number | null }>,
  ): ActivitySourceRow[] {
    const peak = mapped[0]?.hours ?? 1;
    const total = mapped.reduce((sum, row) => sum + row.hours, 0) || 1;
    return mapped.map((row) => ({
      ...row,
      sharePct: (row.hours / total) * 100,
      barPct: Math.max(8, (row.hours / peak) * 100),
      deltaHours: row.previousHours == null ? null : row.hours - row.previousHours,
    }));
  }

  private isWebSource(type: string): boolean {
    const normalized = type.trim().toLowerCase();
    return normalized === 'web' || normalized === 'website' || normalized === 'browser';
  }

  private buildVerdict(bars: TrendBar[]): string {
    const meaningful = bars.filter((b) => b.shape !== 'off' && b.shape !== 'light');
    if (!meaningful.length) return 'No meaningful tracked activity in this window.';
    const longDays = bars.filter((b) => b.shape === 'long').length;
    const shortDays = bars.filter((b) => b.shape === 'short').length;
    if (meaningful.length < 8) {
      return `Tracked activity on ${meaningful.length} of ${this.windowDays} days — a sparse window, not a daily rhythm.`;
    }
    if (longDays >= 5) {
      return `${longDays} days ran longer than this person's typical day. Colors are relative to them, not a company score.`;
    }
    if (shortDays >= 5 && shortDays > longDays) {
      return `Most active days were shorter than this person's typical day.`;
    }
    return 'Active days clustered around this person\'s typical pace.';
  }

  private displaySourceName(raw: unknown): string {
    const value = String(raw ?? '').trim();
    if (!value) return 'Unknown';
    return value.replace(/^https?:\/\//i, '').replace(/^www\./i, '').replace(/\/$/, '');
  }

  private median(values: number[]): number {
    if (!values.length) return 0;
    const sorted = [...values].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
  }

  private formatDate(value: Date): string {
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private daysAgo(days: number): string {
    const date = new Date();
    date.setDate(date.getDate() - days);
    return this.formatDate(date);
  }

  private shortDate(value: string): string {
    const parts = String(value ?? '').split('-');
    return parts.length === 3 ? `${parts[1]}/${parts[2]}` : String(value ?? '');
  }
}
